import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from "react";
// contexts
import {
    NEWRELIC_TABLES,
    recordNREvent,
    GlobalHeaderState,
    SwgyHeaderKeysList,
    isJioHotstarFlow,
} from "@swiggy-private/checkout-core";
import { CartIPLSwiggySixesContext, isMWebPlatform } from "@swiggy-private/core-ui";
// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { isValidNumber } from "@swiggy-private/core-ui";
import { Storage } from "@swiggy-private/core-ui";
// types
import { SwiggySixesCommState, TimerUpdateSource } from "@swiggy-private/cart-types";
// hooks
import { useIPLSwiggySixesTickerTimer } from "@cart-client/hooks/useIPLSwiggySixesTickerTimer";
import { useIPLSwiggySixesJSBridge } from "@cart-client/hooks/useIPLSwiggySixesJSBridge";
import { useIPLSwiggySixesPolling } from "@cart-client/hooks/useIPLSwiggySixesPolling";
import { TIMEOUT_TO_HIDE_CROUTON_AFTER_TIMER_EXPIRED, NR_EVENT_TYPE } from "./constants";

const STORAGE_KEY = "swgy_ipl_swiggy_sixes_crouton_session_key";

interface CartIPLSwiggySixesProviderWrapperProps {
    children: React.ReactNode;
}

const CartIPLSwiggySixesProviderWrapper = ({ children }: CartIPLSwiggySixesProviderWrapperProps) => {
    const timeoutToHideCroutonAfterExpiry = useRef<NodeJS.Timeout>();
    const [timestampInMs, setTimestampInMs] = useState<number>();
    const [croutonCommsState, setCroutonCommsState] = useState<SwiggySixesCommState>(SwiggySixesCommState.DISABLED);
    const [updateSource, setUpdateSource] = useState<TimerUpdateSource>();
    const platform = GlobalHeaderState.getHeader(SwgyHeaderKeysList.platform) || "";
    const isMWeb = useMemo(() => isMWebPlatform(platform), [platform]);

    const setCroutonTrackingInSession = useCallback((value: boolean) => {
        Storage.setItem(STORAGE_KEY, value);
    }, []);

    const isCroutonEnabledInSession = useCallback(() => {
        return Boolean(Storage.getItem(STORAGE_KEY));
    }, []);

    const cleanupTimeoutToHideCroutonAfterExpiry = useCallback(() => {
        if (!isEmpty(timeoutToHideCroutonAfterExpiry.current)) {
            clearTimeout(timeoutToHideCroutonAfterExpiry.current);
            timeoutToHideCroutonAfterExpiry.current = undefined;
        }
    }, []);

    const timestampUpdateNRLog = useCallback(
        (accepted = false, timestampInSec?: number, source?: TimerUpdateSource) => {
            recordNREvent({
                eventType: NEWRELIC_TABLES.IPL_SWIGGY_SIXES_DEBUG,
                type: NR_EVENT_TYPE.INCOMING_TIMER,
                accepted: String(accepted),
                timestampInSec: String(timestampInSec || ""),
                clientTimeInSec: String(Math.ceil(Date.now() / 1000)),
                source: source || "",
            });
        },
        [],
    );

    const { tickerTimerInMinSec, cleanupTickerTimer, disableTickerTimerAndComms } = useIPLSwiggySixesTickerTimer(
        timestampInMs,
        setCroutonTrackingInSession,
    );

    // incoming timer will be in seconds from all sources - apps + update-cart API + fetch-coupons API
    // validate input and compare with in memory timer before setting
    const validateAndSetTimestamp = useCallback(
        (inputTimeStampInSec?: number, updateSource?: TimerUpdateSource) => {
            if (
                isEmpty(inputTimeStampInSec) ||
                !isValidNumber(inputTimeStampInSec) ||
                // if incoming input timer is already outdated
                inputTimeStampInSec <= Math.ceil(Date.now() / 1000)
            ) {
                timestampUpdateNRLog(false, inputTimeStampInSec, updateSource);
                return;
            } else if (
                isEmpty(timestampInMs) ||
                !isValidNumber(timestampInMs) ||
                timestampInMs < inputTimeStampInSec * 1000
            ) {
                // setting comms as we are validating incoming timer with comparison from internal timer
                if (isValidNumber(timestampInMs) && tickerTimerInMinSec.enabled) {
                    // if existing timestamp was present and new timestamp is passed, this is CONSECUTIVE_SIXES_HIT state as this is another six case
                    // when a previous six was already present
                    setCroutonCommsState(SwiggySixesCommState.CONSECUTIVE_SIXES_HIT);
                } else {
                    // if existing timestamp is empty and new timestamp is valid, this is SIX_HIT state as this is a brand new six hit
                    // if existing timestamp is valid but timer is disabled, this is SIX_HIT state as this is a brand new six hit
                    setCroutonCommsState(SwiggySixesCommState.SIX_HIT);
                }
                // suppose six timer got exired and after X seconds crouton will be hidden if another six was hit in this time crouton would get hidden
                // hence for second trigger case we need to disable hiding crouton if another six came in later
                setUpdateSource(updateSource);
                cleanupTimeoutToHideCroutonAfterExpiry();
                setTimestampInMs(Math.ceil(Number(inputTimeStampInSec)) * 1000);
                timestampUpdateNRLog(true, inputTimeStampInSec, updateSource);
            }
        },
        [timestampInMs, timestampUpdateNRLog, cleanupTimeoutToHideCroutonAfterExpiry, tickerTimerInMinSec.enabled],
    );

    const { cleanupAppJSBridgeHandler } = useIPLSwiggySixesJSBridge({ validateAndSetTimestamp });

    // Polling API for mweb
    useIPLSwiggySixesPolling({
        validateAndSetTimestamp,
        isEnabled: isMWeb && isJioHotstarFlow(),
    });

    const hasTimerReachedExpiry = useCallback(() => {
        const { minutes = 0, seconds = 0 } = tickerTimerInMinSec;
        // had to make this expiry check at <= 1 to allow correct crouton state updates,
        // at exactly = 0 check the state update for comms was missed to refresh the crouton
        return tickerTimerInMinSec.enabled && minutes === 0 && seconds <= 1;
    }, [tickerTimerInMinSec]);

    useEffect(() => {
        // if the enabled ticker reaches 0 in that case we need to show EXPIRED state
        if (hasTimerReachedExpiry() && isEmpty(timeoutToHideCroutonAfterExpiry.current)) {
            setCroutonCommsState(SwiggySixesCommState.EXPIRED);
            timeoutToHideCroutonAfterExpiry.current = setTimeout(() => {
                disableTickerTimerAndComms();
                setTimestampInMs(undefined);
                setCroutonCommsState(SwiggySixesCommState.DISABLED);
                // Clear source with the timer so isFromJSBridge cannot stick across cycles.
                setUpdateSource(undefined);
                timeoutToHideCroutonAfterExpiry.current = undefined;
            }, TIMEOUT_TO_HIDE_CROUTON_AFTER_TIMER_EXPIRED);
        }
    }, [disableTickerTimerAndComms, hasTimerReachedExpiry]);

    return (
        <CartIPLSwiggySixesContext.Provider
            value={{
                timestampInMs,
                tickerTimerInMinSec,
                croutonCommsState,
                validateAndSetTimestamp,
                cleanupTickerTimer,
                disableTickerTimerAndComms,
                cleanupAppJSBridgeHandler,
                setCroutonTrackingInSession,
                isCroutonEnabledInSession,
                updateSource,
            }}
        >
            {children}
        </CartIPLSwiggySixesContext.Provider>
    );
};

export default memo(CartIPLSwiggySixesProviderWrapper);
